import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export const fetchProducts = async () => {
  const { data } = await api.get("/products");
  return data.products;
};

export const fetchProduct = async (slug) => {
  const { data } = await api.get(`/products/${slug}`);
  return data;
};

export const subscribeNewsletter = async (email, lang = "fr") => {
  const { data } = await api.post("/newsletter", { email, lang });
  return data;
};

export const joinWaitlist = async ({ email, product_id, variant_id, lang = "fr" }) => {
  const { data } = await api.post("/waitlist", { email, product_id, variant_id, lang });
  return data;
};

export const submitContact = async (payload) => {
  const { data } = await api.post("/contact", payload);
  return data;
};

export const checkout = async (payload) => {
  const { data } = await api.post("/checkout", payload);
  return data;
};

export const createStripeCheckoutSession = async ({ items, email, phone, lang = "fr", origin_url }) => {
  const { data } = await api.post("/stripe/checkout-session", {
    items,
    email,
    phone,
    lang,
    origin_url,
  });
  return data;
};

export const getStripeCheckoutStatus = async (sessionId) => {
  const { data } = await api.get(`/stripe/checkout-status/${sessionId}`);
  return data;
};

// ---------- ADMIN ----------
const ADMIN_KEY_STORAGE = "flv_admin_token";

export const adminGetToken = () => {
  try { return localStorage.getItem(ADMIN_KEY_STORAGE) || ""; } catch { return ""; }
};

export const adminSetToken = (token) => {
  try {
    if (token) localStorage.setItem(ADMIN_KEY_STORAGE, token);
    else localStorage.removeItem(ADMIN_KEY_STORAGE);
  } catch (_) { /* noop */ }
};

const adminHeaders = () => ({ headers: { "X-Admin-Key": adminGetToken() } });

export const adminLogin = async (password) => {
  const { data } = await api.post("/admin/login", { password });
  adminSetToken(data.token);
  return data;
};

export const adminStats = async () => {
  const { data } = await api.get("/admin/stats", adminHeaders());
  return data;
};

export const adminListOrders = async ({ status = "all", search = "", limit = 100, skip = 0 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  params.set("limit", String(limit));
  params.set("skip", String(skip));
  const { data } = await api.get(`/admin/orders?${params.toString()}`, adminHeaders());
  return data;
};

export const adminOrderDetail = async (orderNumber) => {
  const { data } = await api.get(`/admin/orders/${orderNumber}`, adminHeaders());
  return data;
};

export const adminMarkShipped = async (orderNumber, payload) => {
  const { data } = await api.post(`/admin/orders/${orderNumber}/shipped`, payload, adminHeaders());
  return data;
};

export const adminCsvUrl = () => {
  const base = (api.defaults.baseURL || "").replace(/\/$/, "");
  // CSV is downloaded directly with the token in URL? No — we send via header.
  // Instead expose URL + caller can fetch with header.
  return `${base}/admin/orders/export.csv`;
};

export const adminDownloadCsv = async (statusFilter = "all") => {
  const params = statusFilter && statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "";
  const { data } = await api.get(`/admin/orders/export.csv${params}`, {
    ...adminHeaders(),
    responseType: "blob",
  });
  return data;
};

export const adminListSubscribers = async ({ limit = 200, skip = 0 } = {}) => {
  const { data } = await api.get(`/admin/newsletter/list?limit=${limit}&skip=${skip}`, adminHeaders());
  return data;
};

export const adminDownloadInvoice = async (orderNumber) => {
  const { data } = await api.get(`/admin/orders/${encodeURIComponent(orderNumber)}/invoice.pdf`, {
    ...adminHeaders(),
    responseType: "blob",
  });
  return data;
};

export const adminSendInvoiceEmail = async (orderNumber) => {
  const { data } = await api.post(
    `/admin/orders/${encodeURIComponent(orderNumber)}/send-invoice`,
    {},
    adminHeaders()
  );
  return data;
};

export const adminMarkDelivered = async (orderNumber) => {
  const { data } = await api.post(
    `/admin/orders/${encodeURIComponent(orderNumber)}/mark-delivered`,
    {},
    adminHeaders()
  );
  return data;
};

export const adminCancelReviewEmail = async (orderNumber) => {
  const { data } = await api.post(
    `/admin/orders/${encodeURIComponent(orderNumber)}/cancel-review-email`,
    {},
    adminHeaders()
  );
  return data;
};

// ---------- GDPR / SELF-SERVE DATA ----------
export const requestDataAccess = async (email, lang = "fr") => {
  const { data } = await api.post("/me/request-access", {
    email,
    lang,
    origin_url: typeof window !== "undefined" ? window.location.origin : "",
  });
  return data;
};

export const fetchMyData = async (token) => {
  const { data } = await api.get(`/me/data?token=${encodeURIComponent(token)}`);
  return data;
};

export const deleteMyData = async (token) => {
  const { data } = await api.post(`/me/delete?token=${encodeURIComponent(token)}`);
  return data;
};

export const exportMyDataUrl = (token) => {
  const base = (api.defaults.baseURL || "").replace(/\/$/, "");
  return `${base}/me/export?token=${encodeURIComponent(token)}`;
};

// ---------- REVIEWS ----------
export const fetchReviews = async (productId) => {
  const { data } = await api.get(`/reviews/${productId}`);
  return data;
};

export const submitReview = async (payload) => {
  const { data } = await api.post("/reviews", payload);
  return data;
};

export const uploadReviewPhoto = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/reviews/upload-photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { ok, photo_url }
};

export const reviewPhotoUrl = (storagePath) => {
  if (!storagePath) return null;
  if (storagePath.startsWith("http://") || storagePath.startsWith("https://")) return storagePath;
  return `${API}/reviews/photo/${storagePath}`;
};
